import { Text, ActionIcon, Box, Select } from '@mantine/core';
import { IconTrash, IconCheck, IconX } from '@tabler/icons-react';
import { LicenceRecord, UserRole, isFieldEditable } from '@lms/shared';
import { Card } from './Card';
import { DividerGrid } from './DividerGrid';
import { FormField } from './FormField';
import { InlineEditor } from './InlineEditor';
import { useState, useEffect } from 'react';

interface ApiKeyStatusEditorProps {
  enabled?: boolean;
  editable: boolean;
  onSave?: (enabled: boolean) => void;
}

const ApiKeyStatusEditor = ({ enabled, editable, onSave }: ApiKeyStatusEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(enabled ? 'enabled' : 'disabled');

  useEffect(() => {
    setEditValue(enabled ? 'enabled' : 'disabled');
  }, [enabled]);

  const handleClick = () => {
    if (editable) {
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    onSave?.(editValue === 'enabled');
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(enabled ? 'enabled' : 'disabled');
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <Box
        onClick={handleClick}
        style={{
          cursor: editable ? 'pointer' : 'default',
          padding: '6px 10px',
          borderRadius: 'var(--mantine-radius-sm)',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: editable ? 'transparent' : undefined,
          transition: 'all 0.2s ease',
          border: '1px solid transparent',
        }}
        onMouseEnter={(e) => {
          if (editable) {
            e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-0)';
            e.currentTarget.style.borderColor = 'var(--mantine-color-blue-2)';
          }
        }}
        onMouseLeave={(e) => {
          if (editable) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          }
        }}
        tabIndex={editable ? 0 : -1}
        role={editable ? 'button' : undefined}
      >
        <Text size="sm" c={enabled ? 'green' : 'red'} fw={600}>
          {enabled ? 'Enabled' : 'Disabled'}
        </Text>
      </Box>
    );
  }

  return (
    <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <Select
        size="sm"
        value={editValue}
        onChange={(value) => setEditValue(value || 'disabled')}
        data={[
          { value: 'enabled', label: 'Enabled' },
          { value: 'disabled', label: 'Disabled' },
        ]}
        styles={{
          input: {
            color: editValue === 'enabled' ? 'green' : 'red',
            fontWeight: 600,
          },
        }}
        autoFocus
      />
      <ActionIcon color="green" variant="filled" onClick={handleSave} size="sm">
        <IconCheck size={16} />
      </ActionIcon>
      <ActionIcon color="red" variant="filled" onClick={handleCancel} size="sm">
        <IconX size={16} />
      </ActionIcon>
    </Box>
  );
};

interface LicenceDetailsProps {
  licence: LicenceRecord;
  userRole?: UserRole;
  onUpdate?: (updates: Partial<LicenceRecord>) => void;
  onDelete?: () => void;
  apiKeyStatus?: { enabled?: boolean };
  onApiKeyStatusUpdate?: (enabled: boolean) => void;
}

export const LicenceDetails = ({ licence, userRole = 'Viewer', onUpdate, onDelete, apiKeyStatus, onApiKeyStatusUpdate }: LicenceDetailsProps) => {
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const handleFieldUpdate = async (field: keyof LicenceRecord, newValue: any) => {
    if (onUpdate) {
      onUpdate({ [field]: newValue });
    }
  };

  return (
    <>
      <Card 
        title="Licence Details" 
        description={licence.last_updated ? (
          <span>
            Last updated: {new Date(licence.last_updated).toLocaleString('en-GB', { 
              timeZone: 'Europe/London',
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })} (LON) | {new Date(licence.last_updated).toLocaleString('en-AU', { 
              timeZone: 'Australia/Sydney',
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })} (SYD)
          </span>
        ) : "Complete details for the selected licence record"}
        actions={onDelete && (
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={onDelete}
            aria-label={`Delete licence for ${licence.customer_key}`}
            title={`Delete licence for ${licence.customer_key}`}
          >
            <IconTrash size={18} />
          </ActionIcon>
        )}
      >
      {/* Status field - full width, prominent display */}
      <Box 
        mb="lg" 
        p="md" 
        style={{ 
          backgroundColor: licence.status === 'ACTIVE' ? '#e7f5e7' :
                           licence.status === 'INACTIVE' ? '#ffe7e7' :
                           licence.status === 'PENDING' ? '#fff4e6' :
                           licence.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? '#fffbe6' :
                           '#f5f5f5',
          border: `2px solid ${licence.status === 'ACTIVE' ? '#51cf66' :
                                licence.status === 'INACTIVE' ? '#ff6b6b' :
                                licence.status === 'PENDING' ? '#fd7e14' :
                                licence.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? '#fab005' :
                                '#adb5bd'}`,
          borderRadius: '8px',
          textAlign: 'center'
        }}
      >
        {isFieldEditable(userRole, 'status') ? (
          <InlineEditor
            field="status"
            value={licence.status}
            editable={true}
            onSave={async (newValue) => await handleFieldUpdate('status', newValue)}
            customDisplay={
              <Text 
                size="lg" 
                c={licence.status === 'ACTIVE' ? 'green' :
                   licence.status === 'INACTIVE' ? 'red' :
                   licence.status === 'PENDING' ? 'orange' :
                   licence.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? 'yellow' :
                   'gray'}
                fw={700}
                ta="center"
              >
                {licence.status === 'ACTIVE' ? 'Active' :
                 licence.status === 'INACTIVE' ? 'Inactive' :
                 licence.status === 'PENDING' ? 'Pending' :
                 licence.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? 'Active (No Hub)' :
                 licence.status}
              </Text>
            }
          />
        ) : (
          <Text 
            size="lg" 
            c={licence.status === 'ACTIVE' ? 'green' :
               licence.status === 'INACTIVE' ? 'red' :
               licence.status === 'PENDING' ? 'orange' :
               licence.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? 'yellow' :
               'gray'}
            fw={700}
            ta="center"
          >
            {licence.status === 'ACTIVE' ? 'Active' :
             licence.status === 'INACTIVE' ? 'Inactive' :
             licence.status === 'PENDING' ? 'Pending' :
             licence.status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? 'Active (No Hub)' :
             licence.status}
          </Text>
        )}
      </Box>

      <DividerGrid columns={3}>
        <FormField label="Licence Type">
          <InlineEditor
            field="licence_type"
            value={licence.licence_type}
            editable={isFieldEditable(userRole, 'licence_type')}
            onSave={async (newValue) => await handleFieldUpdate('licence_type', newValue)}
            customDisplay={
              <Text 
                size="sm" 
                c={licence.licence_type === 'MARKETPLACE' ? 'blue' : 'orange'}
                fw={600}
              >
                {licence.licence_type === 'MARKETPLACE' ? 'Marketplace' : 
                 licence.licence_type === 'PRIVATE' ? 'Private' : 
                 licence.licence_type}
              </Text>
            }
          />
        </FormField>
        
        <FormField label="Accounts">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text size="sm" c="dimmed">
              {licence.current_account_count || 0}
            </Text>
            <Text size="sm" c="dimmed">/</Text>
            <InlineEditor
              field="max_accounts"
              value={licence.max_accounts}
              editable={isFieldEditable(userRole, 'max_accounts')}
              onSave={async (newValue) => await handleFieldUpdate('max_accounts', newValue)}
              customDisplay={
                <Text size="lg" fw={700}>
                  {licence.max_accounts || 0}
                </Text>
              }
            />
          </div>
        </FormField>
        
        <FormField label="Valid Through">
          <InlineEditor
            field="valid_through"
            value={licence.valid_through}
            editable={isFieldEditable(userRole, 'valid_through')}
            onSave={async (newValue) => await handleFieldUpdate('valid_through', newValue)}
            customDisplay={licence.valid_through ? (
              <div style={{ fontSize: '14px', lineHeight: '1.3' }}>
                <div>
                  {new Date(licence.valid_through).toLocaleString('en-GB', { 
                    timeZone: 'Europe/London',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} (LON)
                </div>
                <div style={{ color: '#666' }}>
                  {new Date(licence.valid_through).toLocaleString('en-AU', { 
                    timeZone: 'Australia/Sydney',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} (SYD)
                </div>
              </div>
            ) : (
              <Text size="sm">-</Text>
            )}
          />
        </FormField>
        <FormField label="Product Code">
          <Text size="sm">{licence.product_code || '-'}</Text>
        </FormField>
        <FormField label="Customer Identifier">
          <InlineEditor
            field="customer_identifier"
            value={licence.customer_identifier}
            editable={isFieldEditable(userRole, 'customer_identifier')}
            onSave={async (newValue) => await handleFieldUpdate('customer_identifier', newValue)}
          />
        </FormField>
        <FormField label="Entitlements">
          {isFieldEditable(userRole, 'entitlements') ? (
            <InlineEditor
              field="entitlements"
              value={formatValue(licence.entitlements)}
              editable={true}
              onSave={async (newValue) => {
                try {
                  const parsed = JSON.parse(newValue);
                  await handleFieldUpdate('entitlements', parsed);
                } catch {
                  await handleFieldUpdate('entitlements', newValue);
                }
              }}
              customDisplay={licence.entitlements && Array.isArray(licence.entitlements) && licence.entitlements.length > 0 ? (
                <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  {licence.entitlements.map((entitlement, index) => (
                    <div key={index} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      {entitlement.account_count && (
                        <div><strong>Accounts:</strong> {entitlement.account_count}</div>
                      )}
                      {entitlement.expiration_date && (
                        <div><strong>Expires:</strong> {new Date(entitlement.expiration_date).toLocaleDateString('en-GB', { 
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <Text size="sm">-</Text>
              )}
            />
          ) : licence.entitlements && Array.isArray(licence.entitlements) && licence.entitlements.length > 0 ? (
            <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
              {licence.entitlements.map((entitlement, index) => (
                <div key={index} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {entitlement.account_count && (
                    <div><strong>Accounts:</strong> {entitlement.account_count}</div>
                  )}
                  {entitlement.expiration_date && (
                    <div><strong>Expires:</strong> {new Date(entitlement.expiration_date).toLocaleDateString('en-GB', { 
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Text size="sm">-</Text>
          )}
        </FormField>
      </DividerGrid>
    </Card>
    
    {/* API Access Card */}
    <Card title="API Access" description="API authentication and access credentials" mt="lg">
      <DividerGrid columns={3}>
        <FormField label="Customer Key">
          <Text size="sm">{licence.customer_key || '-'}</Text>
        </FormField>
        
        <FormField label="API Gateway Key ID">
          <Text size="sm">{licence.api_gw_key_id || '-'}</Text>
        </FormField>
        
        <FormField label="API Key Status">
          <ApiKeyStatusEditor
            enabled={apiKeyStatus?.enabled}
            editable={!!(licence.api_gw_key_id && onApiKeyStatusUpdate)}
            onSave={onApiKeyStatusUpdate}
          />
        </FormField>
      </DividerGrid>
    </Card>
    
    {/* Customer Details Card */}
    <Card title="Customer Details" description="Customer information and account details" mt="lg">
      {/* Version field - full width, prominent display */}
      <Box 
        mb="lg" 
        p="md" 
        style={{ 
          backgroundColor: '#e7f3ff',
          border: '2px solid #339af0',
          borderRadius: '8px',
          textAlign: 'center'
        }}
      >
        {isFieldEditable(userRole, 'version') ? (
          <InlineEditor
            field="version"
            value={licence.version}
            editable={true}
            onSave={async (newValue) => await handleFieldUpdate('version', newValue)}
            customDisplay={
              <Text 
                size="lg" 
                c="blue"
                fw={700}
                ta="center"
              >
                {licence.version || 'Not defined'}
              </Text>
            }
          />
        ) : (
          <Text 
            size="lg" 
            c="blue"
            fw={700}
            ta="center"
          >
            {licence.version || 'Not defined'}
          </Text>
        )}
      </Box>

      <DividerGrid columns={4}>
        <FormField label="Registration Email">
          <InlineEditor
            field="registration_email"
            value={licence.registration_email}
            editable={isFieldEditable(userRole, 'registration_email')}
            onSave={async (newValue) => await handleFieldUpdate('registration_email', newValue)}
          />
        </FormField>
        
        <FormField label="Customer Details">
          {isFieldEditable(userRole, 'customer_details') ? (
            <InlineEditor
              field="customer_details"
              value={formatValue(licence.customer_details)}
              editable={true}
              onSave={async (newValue) => {
                try {
                  const parsed = JSON.parse(newValue);
                  await handleFieldUpdate('customer_details', parsed);
                } catch {
                  await handleFieldUpdate('customer_details', newValue);
                }
              }}
              customDisplay={licence.customer_details ? (
                <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  <div><strong>Name:</strong> {licence.customer_details.name}</div>
                  <div><strong>Email:</strong> {licence.customer_details.email}</div>
                  <div><strong>Company:</strong> {licence.customer_details.company}</div>
                </div>
              ) : (
                <Text size="sm">-</Text>
              )}
            />
          ) : licence.customer_details ? (
            <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
              <div><strong>Name:</strong> {licence.customer_details.name}</div>
              <div><strong>Email:</strong> {licence.customer_details.email}</div>
              <div><strong>Company:</strong> {licence.customer_details.company}</div>
            </div>
          ) : (
            <Text size="sm">-</Text>
          )}
        </FormField>
        
        <FormField label="Hub AWS Account">
          <InlineEditor
            field="hub_aws_account_id"
            value={licence.hub_aws_account_id}
            editable={isFieldEditable(userRole, 'hub_aws_account_id')}
            onSave={async (newValue) => await handleFieldUpdate('hub_aws_account_id', newValue)}
          />
        </FormField>
        
        <FormField label="Purchaser AWS Account">
          <InlineEditor
            field="purchaser_aws_account_id"
            value={licence.purchaser_aws_account_id}
            editable={isFieldEditable(userRole, 'purchaser_aws_account_id')}
            onSave={async (newValue) => await handleFieldUpdate('purchaser_aws_account_id', newValue)}
          />
        </FormField>
        

        
      </DividerGrid>
    </Card>
    
    {/* Session Details Card */}
    <Card title="Session Details" description="Session token and expiry information" mt="lg">
      <DividerGrid columns={2}>
        <FormField label="Session Token">
          <InlineEditor
            field="session_token"
            value={licence.session_token}
            editable={isFieldEditable(userRole, 'session_token')}
            onSave={async (newValue) => await handleFieldUpdate('session_token', newValue)}
          />
        </FormField>
        
        <FormField label="Session Expiry">
          <InlineEditor
            field="session_expiry"
            value={licence.session_expiry}
            editable={isFieldEditable(userRole, 'session_expiry')}
            onSave={async (newValue) => await handleFieldUpdate('session_expiry', newValue)}
            customDisplay={licence.session_expiry ? (
              <div style={{ fontSize: '14px', lineHeight: '1.3' }}>
                <div>
                  {new Date(licence.session_expiry).toLocaleString('en-GB', { 
                    timeZone: 'Europe/London',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} (LON)
                </div>
                <div style={{ color: '#666' }}>
                  {new Date(licence.session_expiry).toLocaleString('en-AU', { 
                    timeZone: 'Australia/Sydney',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} (SYD)
                </div>
              </div>
            ) : (
              <Text size="sm">-</Text>
            )}
          />
        </FormField>
      </DividerGrid>
    </Card>
    </>
  );
};
